import { useCallback, useMemo, type ComponentProps } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { Button, ControlledInput, ControlledSelect } from '@shared/ui';
import { useBonusUsers, useManualBonusAdjust } from '../../hooks/useBonuses';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import { formatPhoneNumber } from '@shared/utils';
import {
  manualBonusFormSchema,
  DEFAULT_MANUAL_COMMENT,
  type ManualBonusFormValues,
} from './manualBonusFormSchema';

const DEFAULT_FORM_VALUES: ManualBonusFormValues = {
  sellerId: null,
  userId: null,
  type: 'accrue',
  amountType: 'arbitrary',
  amount: undefined,
  orderAmount: undefined,
  expiresDays: undefined,
  description: DEFAULT_MANUAL_COMMENT,
};

export default function ManualBonusForm() {
  const { data: users = [], isLoading: usersLoading } = useBonusUsers();
  const { data: sellers = [], isLoading: sellersLoading } = useQuery({
    queryKey: ['sellers', 'list-for-bonuses'],
    queryFn: () => sellersApi.getAll(),
  });
  const manualAdjust = useManualBonusAdjust();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ManualBonusFormValues>({
    resolver: yupResolver(manualBonusFormSchema) as any,
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const type = watch('type');
  const amountType = watch('amountType');

  const onSubmit = async (data: ManualBonusFormValues) => {
    const payload: Parameters<typeof manualAdjust.mutateAsync>[0] = {
      userId: Number(data.userId),
      sellerId: Number(data.sellerId),
      type: data.type as 'accrue' | 'deduct',
      amount: 0,
      description: data.description || DEFAULT_MANUAL_COMMENT,
    };
    if (data.type === 'accrue') {
      payload.amountType = data.amountType as 'arbitrary' | 'from_order';
      if (data.amountType === 'from_order') {
        payload.orderAmount = Number(data.orderAmount);
      } else {
        payload.amount = Number(data.amount);
        if (data.expiresDays != null && data.expiresDays > 0) {
          payload.expiresDays = data.expiresDays;
        }
      }
    } else {
      payload.amount = Number(data.amount);
    }
    await manualAdjust.mutateAsync(payload);
    reset();
  };

  const sellerOptions = useMemo(
    () =>
      sellers.map((s) => ({
        value: String(s.id),
        label: s.shopName?.trim() ? `${s.shopName} (#${s.id})` : `Магазин #${s.id}`,
        searchName: (s.shopName ?? '').toLowerCase(),
      })),
    [sellers],
  );

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: String(u.id),
        label: u.name ? `${formatPhoneNumber(u.phone)} (${u.name})` : formatPhoneNumber(u.phone),
        rawPhone: u.phone,
        searchName: (u.name ?? '').toLowerCase(),
      })),
    [users],
  );

  const filterOptions = useCallback(
    (input: { options: unknown[]; search: string }) => {
      const { options, search } = input;
      if (!search.trim()) return options;
      const searchLower = search.toLowerCase().trim();
      const searchDigits = search.replace(/\D/g, '');
      return options.filter((option: unknown) => {
        if (!option || typeof option !== 'object' || !('label' in option)) return true;
        const o = option as { label?: string; searchName?: string; rawPhone?: string };
        const labelMatch = (o.label ?? '').toString().toLowerCase().includes(searchLower);
        const nameMatch = (o.searchName ?? '').toString().includes(searchLower);
        const phoneDigits = o.rawPhone != null ? String(o.rawPhone).replace(/\D/g, '') : '';
        const phoneMatch = searchDigits.length > 0 && phoneDigits.includes(searchDigits);
        return labelMatch || nameMatch || phoneMatch;
      });
    },
    [],
  );

  return (
    <section className="bonuses-card">
      <div className="bonuses-card__head">
        <h2 className="bonuses-card__title">Ручное начисление</h2>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="bonuses-manual-grid">
          <div className="bonuses-form-field">
            <ControlledSelect
              label="Продавец"
              placeholder="Выберите магазин"
              name="sellerId"
              control={control}
              error={errors.sellerId}
              data={sellerOptions}
              searchable
              clearable
              disabled={sellersLoading}
              nothingFoundMessage="Продавцы не найдены"
              filter={filterOptions as ComponentProps<typeof ControlledSelect>['filter']}
            />
          </div>
          <div className="bonuses-form-field">
            <ControlledSelect
              label="Пользователь"
              placeholder="Найдите по номеру"
              name="userId"
              control={control}
              error={errors.userId}
              data={userOptions}
              searchable
              clearable
              disabled={usersLoading}
              nothingFoundMessage="Пользователи не найдены"
              filter={filterOptions as ComponentProps<typeof ControlledSelect>['filter']}
            />
          </div>
          <div className="bonuses-form-field">
            <ControlledSelect
              label="Операция"
              name="type"
              control={control}
              error={errors.type}
              data={[
                { value: 'accrue', label: 'Начислить' },
                { value: 'deduct', label: 'Списать' },
              ]}
            />
          </div>
          {type === 'accrue' && (
            <div className="bonuses-form-field">
              <ControlledSelect
                label="Тип начисления"
                name="amountType"
                control={control}
                error={errors.amountType}
                data={[
                  { value: 'arbitrary', label: 'Произвольная сумма' },
                  { value: 'from_order', label: 'От суммы заказа' },
                ]}
              />
            </div>
          )}
          {type === 'deduct' && (
            <div className="bonuses-form-field">
              <ControlledInput
                id="manual-amount"
                type="number"
                label="Сколько списать"
                name="amount"
                control={control}
                error={errors.amount}
                placeholder="Бонусов"
                min={1}
                step={1}
              />
            </div>
          )}
          {type === 'accrue' && amountType === 'from_order' && (
            <div className="bonuses-form-field">
              <ControlledInput
                id="manual-orderAmount"
                type="number"
                label="Сумма заказа (₽)"
                name="orderAmount"
                control={control}
                error={errors.orderAmount}
                placeholder="Сумма заказа"
                min={0}
                step={100}
              />
            </div>
          )}
          {type === 'accrue' && amountType === 'arbitrary' && (
            <>
              <div className="bonuses-form-field">
                <ControlledInput
                  id="manual-amount-accrue"
                  type="number"
                  label="Сколько начислить"
                  name="amount"
                  control={control}
                  error={errors.amount}
                  placeholder="Бонусов"
                  min={1}
                  step={1}
                />
              </div>
              <div className="bonuses-form-field">
                <ControlledInput
                  id="manual-expiresDays"
                  type="number"
                  label="Срок действия (дней)"
                  name="expiresDays"
                  control={control}
                  error={errors.expiresDays}
                  placeholder="Не ограничен"
                  min={0}
                  step={1}
                />
              </div>
            </>
          )}
          <div className="bonuses-form-field bonuses-manual-comment">
            <ControlledInput
              id="manual-description"
              label="Комментарий"
              name="description"
              control={control}
              error={errors.description}
              placeholder={DEFAULT_MANUAL_COMMENT}
            />
          </div>
        </div>
        <div className="bonuses-form-actions">
          <Button type="submit" variant="primary" disabled={manualAdjust.isPending} loading={manualAdjust.isPending}>
            Выполнить
          </Button>
        </div>
      </form>
    </section>
  );
}
